import { action, computed, makeObservable } from 'mobx';

import { IFormFieldOptions } from '@/stores/interfaces/IFormField';
import {
  DateRangeValue,
  ensureValidDateRangeState,
  formatDateRangeLabel,
  getPeriodPointEnd,
  getPeriodPointStart,
} from '@/utils/dateRange';

import { FieldStore } from '../FieldStore';

export type DateRangeFieldState = {
  fromYear: number | null;
  fromMonth: number | null;
  toYear: number | null;
  toMonth: number | null;
};

const EMPTY_STATE: DateRangeFieldState = {
  fromYear: null,
  fromMonth: null,
  toYear: null,
  toMonth: null,
};

export type FieldDateRangeStoreOptions = Partial<IFormFieldOptions<DateRangeFieldState>>;

export class FieldDateRangeStore extends FieldStore<DateRangeFieldState> {
  constructor(options?: FieldDateRangeStoreOptions) {
    super({
      ...options,
      defaultValue: options?.defaultValue ?? EMPTY_STATE,
    });

    makeObservable(this, {
      fromYear: computed,
      fromMonth: computed,
      toYear: computed,
      toMonth: computed,
      dateRangeValue: computed,
      hasFilter: computed,
      label: computed,
      start: computed,
      end: computed,
      setFromYear: action.bound,
      setFromMonth: action.bound,
      setToYear: action.bound,
      setToMonth: action.bound,
    });
  }

  get fromYear(): number | null {
    return this.value.fromYear;
  }

  get fromMonth(): number | null {
    return this.value.fromMonth;
  }

  get toYear(): number | null {
    return this.value.toYear;
  }

  get toMonth(): number | null {
    return this.value.toMonth;
  }

  /** Кортеж [start, end] для API и реакций (раньше было поле `value` у стора периода). */
  get dateRangeValue(): DateRangeValue {
    return [this.start, this.end];
  }

  get hasFilter(): boolean {
    return this.fromYear !== null || this.toYear !== null;
  }

  get label(): string | null {
    return formatDateRangeLabel(this.dateRangeValue);
  }

  get start(): string | null {
    return getPeriodPointStart({ year: this.fromYear, month: this.fromMonth });
  }

  get end(): string | null {
    return getPeriodPointEnd({ year: this.toYear, month: this.toMonth });
  }

  setFromYear(year: number | null): void {
    this.setValue(ensureValidDateRangeState({ ...this.value, fromYear: year, fromMonth: null }), {
      noTouch: true,
    });
  }

  setFromMonth(month: number | null): void {
    this.setValue(ensureValidDateRangeState({ ...this.value, fromMonth: month }), { noTouch: true });
  }

  setToYear(year: number | null): void {
    this.setValue(ensureValidDateRangeState({ ...this.value, toYear: year, toMonth: null }), {
      noTouch: true,
    });
  }

  setToMonth(month: number | null): void {
    this.setValue(ensureValidDateRangeState({ ...this.value, toMonth: month }), { noTouch: true });
  }
}
